import React, { useState } from 'react';
import Alert from 'react-s-alert';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import { observer } from 'mobx-react';
import { SendIcon, GeometryIcon, PartIcon, DeleteIcon } from 'icons';
import { UiPosition, PinConfig } from 'libs/rendering/gltf/config';
import { FrameHeaderHeight } from 'styles/constants';
import { useChatControl } from 'libs/rendering/hooks';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { AnnotationItem } from 'libs/rendering/gltf/components/toolBar';
import { isIterable } from 'libs/rendering/gltf/api/wasmObservable';
import { useContextManager } from 'apps/context/hooks';
import { renderCustomIcon } from 'utils/jsxUtils';
import { SpinnerStyled } from 'components/Button/Button.styled';
import { Button, Icon } from 'components';
import RelativeTime from 'components/RelativeTime';
import { useFrameStore } from 'apps/hooks';
import { useCADManager } from 'hooks';
import { themes } from 'styles/themes/wikifactoryNew';

import keyMap from '../../../../../utils/keyMap';

import {
  PinListStyled,
  PinStyled,
  PinDialog,
  ButtonStyled,
  DialogStyled,
  ButtonNoteHeader,
  TextInputStyled,
  InputStyled,
  PinMessageStyled,
  NoteButtons,
  DialogHeader,
  DialogButtons,
  DialogMessage,
  AvatarStyled,
  SenderName,
} from './pin.styled';

const PinNotes = observer(function _PinNotes() {
  const { file } = useFrameStore() || {};

  const getOffsetY = () => {
    const mimeTypeOffsetYMap = {
      'model/stl': 67,
      'model/step': 69.5,
      'model/stp': 69.5,
      'image/vnd.dxf': 71,
      'model/iges': 71,
    };

    return mimeTypeOffsetYMap[file?.mimeType] ?? 70;
  };

  // position correction constants
  const yIncrement = getOffsetY();
  const yCorrection = PinConfig.pinSize + yIncrement;

  const viewerControl = useViewerControl();

  const { pinSize } = PinConfig;
  const settings = viewerControl.viewer?.settings;
  const currentCamera = settings?.CurrentCamera();

  if (!currentCamera) return null;
  if (!viewerControl.cadReady) return null;
  const tabLoadingStatus = currentCamera?.GetLoadStatus();
  if (tabLoadingStatus !== 0) return null;

  const notes = viewerControl.viewer?.notePad?.GetNotes();
  const noteKeys = notes.keys();

  // - this updates each <Pin/> screen position.
  const updatePinPositions = () => {
    if (!viewerControl.cadReady) return;

    // const currentCamera = settings.CurrentCamera();
    const is3D = currentCamera?.Is3D();
    const is2D = currentCamera?.Is2D();
    if (!is3D && !is2D) return;

    // iterate all pin notes
    if (!isIterable(noteKeys)) {
      return;
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const noteKey of noteKeys) {
      const note = viewerControl.viewer?.notePad?.GetNote(noteKey);
      if (note && note.IsOk() && note.visible) {
        // - I need to have two pins with different z-indexes, so that the dialog
        // is guaranteed to be over the pins
        // - a child will always have the same z-index as its parent, so I needed
        // to make the pin and the dialog silblings instead, I did that by making
        // a second invisible pin and then attaching the dialog to that
        // - pin-styled is the visible pin, pin-dialog is the invisble one that only
        // exists so that the dialog will move together with pin-styled
        const elementIds = [`pin-styled-${note.key}`, `pin-dialog-${note.key}`];
        for (const elementId of elementIds) {
          const el = document.getElementById(elementId);
          if (!el) {
            // eslint-disable-next-line no-continue
            continue;
          }

          if (!viewerControl.viewer.notePad.visible || !note.visible) {
            el.style.display = 'none';
            // eslint-disable-next-line no-continue
            continue;
          }

          // draw only notes for current camera
          if (!viewerControl.pinBelongsToCamera(note, currentCamera)) {
            el.style.display = 'none';
            // eslint-disable-next-line no-continue
            continue;
          }

          // set pointerEvents to none while dragging so that when we drag to
          // rotate the model and the Pin/PinDialog moves under our mouse
          // cursor we continue rotating the model
          if (currentCamera.dragging) {
            el.style.pointerEvents = 'none';
          } else {
            el.style.pointerEvents = 'all';
          }

          // set 2D (screen) coordinates
          const screenPosition = note.GetScreenPosition();
          const dpr = window.devicePixelRatio || 1.0;
          const x = screenPosition.x / dpr;
          const y = screenPosition.y / dpr;
          el.style.left = `${x}px`;
          el.style.top = `${y - yCorrection}px`;
          el.style.display = 'block';

          if (elementId.startsWith('pin-styled-')) {
            const isExpanded = viewerControl.isPinExpanded(note.key);
            // use depth to compute opacity (z)
            if (!isExpanded) {
              let opV = screenPosition.w;
              // 'clamp' extreme opacity values
              if (opV <= PinConfig.pinMinOpacity) opV = PinConfig.pinMinOpacity;
              if (opV >= 0.95) opV = 1;
              el.style.opacity = opV;
            } else {
              el.style.opacity = 1;
            }
          }
        }
      }
    }
  };

  updatePinPositions();

  const pinNotes = [];
  if (isIterable(noteKeys)) {
    // eslint-disable-next-line no-restricted-syntax
    for (const noteKey of noteKeys) {
      const noteObs = viewerControl.viewer?.notePad?.GetNote(noteKey);
      if (!noteObs || !noteObs.IsOk() || !noteObs.visible) {
        continue;
      }

      const forCadItem = viewerControl.findCadItemForNote(noteObs);
      if (!forCadItem) {
        continue;
      }

      // starting 2D (screen) coordinates
      const screenPosition = noteObs.GetScreenPosition();
      const dpr = window.devicePixelRatio || 1.0;
      const x = screenPosition.x / dpr;
      const y = screenPosition.y / dpr;
      noteObs.coords = {
        left: `${x}px`,
        top: `${y - yCorrection}px`,
      };

      pinNotes.push(
        <Pin
          key={`pin-${noteObs.key}`}
          index={0}
          note={noteObs}
          size={pinSize}
        />,
      );
    }
  }

  return <PinListStyled top={FrameHeaderHeight * 2}>{pinNotes}</PinListStyled>;
});

export default PinNotes;

const Pin = observer(function _Pin({ note, size }) {
  const [isHover, setIsHover] = useState(false);
  const { timestamp, userId } = note;
  const isFeature = !note.GetLink()?.GetTarget()?.IsInstance();
  const frameStore = useFrameStore() || {};

  const contextManager = useContextManager();
  const { viewerControl, isActiveGeometry, isActivePart } = useCADManager();
  const chatControl = useChatControl();

  // Use Mobx!
  const [bodyInputText, setBodyInputText] = useState('');

  function expanded() {
    return viewerControl?.isPinExpanded(note.key);
  }

  function hovered() {
    return viewerControl?.isPinHovered(note.key);
  }

  const rightOffset = () => {
    if (
      contextManager &&
      !viewerControl?.isPreview &&
      chatControl?.chatVisible
    ) {
      return UiPosition.panel;
    }
    return 0;
  };

  // PatchManager: add / delete pins
  function deleteAnnotation() {
    // unselect
    viewerControl?.TogglePinExpand(note.key);
    contextManager.DeleteAnnotation(note.annotationId);
  }

  function addAnnotation() {
    // TODO: validate the input
    const textOk = bodyInputText.length > 0;
    if (!textOk) {
      // user message... TODO
      return false;
    }

    const noteObj = viewerControl.viewer?.notePad?.GetNote(note.key);
    if (!noteObj) {
      return false;
    }
    try {
      const targetObj = noteObj.GetItemTargetJsonObj();
      const cameraDataObj = noteObj.GetCameraDataJsonObj();

      const errorHandler = e => {
        Alert.error(
          'There was a problem sending the annotation, please try again later.',
        );
        return contextManager.onSendAnnotationError(e);
      };
      const successHandler = result => {
        setBodyInputText('');
        viewerControl.viewer.selectionManager.ClearIndirectSelection();
        Alert.success('Annotation created.');
        return contextManager.onSendAnnotationSuccess(result);
      };

      const mutation = contextManager.SendAnnotation({
        body: bodyInputText,
        tempKey: note.key,
        target: targetObj,
        camera: cameraDataObj,
        kind: 'NOTE',
        optionsParameter: {
          onSuccess: successHandler,
          onSysErr: errorHandler,
          onUserErr: errorHandler,
        },
      });

      return mutation !== null;
    } catch (e) {
      viewerControl?.LogException(e);
      return false;
    }
  }

  // events
  function onPinClick(e) {
    if (note.isTemp) return;

    viewerControl.TogglePinExpand(note.key);
    e.stopPropagation();
  }

  function handleClose() {
    if (note.isTemp) {
      viewerControl.viewer.notePad.DeleteNote(note.key);
      viewerControl.viewer.selectionManager.ClearIndirectSelection();
    } else {
      viewerControl?.TogglePinExpand(note.key);
    }
    // cancel pin
    viewerControl.SyncSelectionMode();
  }

  function handleBodyTextInput(e) {
    setBodyInputText(e.target.value);
  }

  // function handleReplyTextInput(e) {
  //  setReplyInputText(e.target.value);
  // }

  function handleAddSubmit(e) {
    // eslint-disable-next-line no-unused-expressions
    e?.preventDefault();

    return addAnnotation();
  }

  function onKeyDown(e) {
    switch (e.key.toUpperCase()) {
      case keyMap.ESCAPE:
        break;
      default:
        e.stopPropagation();
    }
  }

  const createdBy =
    contextManager?.members[userId] || frameStore.getUserInfoById(userId);
  const creatorName = createdBy?.username || createdBy?.fullName;
  const canDelete = contextManager?.context?.canDeleteAnnotation(note, userId);
  const shouldShowAnnotationText =
    (isActiveGeometry || isActivePart) && note.isTemp;

  const totalW = window.innerWidth;
  if (!note.coords?.left) return null;
  const leftNum = Number.parseInt(note.coords.left.replace('px', ''), 10);
  const availableW = totalW - leftNum - rightOffset();

  const dialogLeft =
    availableW >= PinConfig.pinDialogWidth
      ? size + PinConfig.pinDialogOffset + 4
      : -(PinConfig.pinDialogOffset + PinConfig.pinDialogWidth);

  const closeButton = (
    <Button
      renderIcon={() => (
        <Icon name='close' size='small' style={{ width: 12, height: 12 }} />
      )}
      variant='tertiary'
      size='xsmall'
      onClick={handleClose}
      appearance='plain'
    />
  );

  let innerPinContent = (
    <>
      {!shouldShowAnnotationText && (
        <PinMessageStyled>
          <ButtonNoteHeader
            icon='close'
            variant='tertiary'
            size='xsmall'
            onClick={handleClose}
            appearance='plain'
          />
        </PinMessageStyled>
      )}
      <form onSubmit={handleAddSubmit}>
        <TextInputStyled>
          <InputStyled
            name='message'
            placeholder='Leave a message...'
            value={bodyInputText}
            onChange={handleBodyTextInput}
            onKeyDown={onKeyDown}
            type='text'
            size='xsmall'
            isGhost
            autoFocus
          />
        </TextInputStyled>
        <NoteButtons>
          <div className='w-full flex justify-end'>
            {contextManager.loading ? (
              <SpinnerStyled size='small' autoColor />
            ) : (
              <div
                onClick={handleAddSubmit}
                data-testid='CADPage-sendAnnotationButton'
                onMouseEnter={() => setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                className='border rounded-full w-5'
                style={{
                  background: isHover ? themes.color.wfGrey100 : 'transparent',
                }}
              >
                <SendIcon />
              </div>
            )}
          </div>
        </NoteButtons>
      </form>
    </>
  );

  if (!note.isTemp) {
    innerPinContent = (
      <>
        <DialogHeader className=' cursor-default'>
          <AvatarStyled
            src={createdBy?.avatar?.url}
            name={createdBy?.fullName || createdBy?.username}
          />
          {creatorName && (
            <SenderName level={4} size='xsmall'>
              {creatorName}
            </SenderName>
          )}
          <RelativeTime date={timestamp} />
          <DialogButtons>
            <div className='flex justify-end items-center'>
              {canDelete && (
                <Button
                  renderIcon={hover =>
                    renderCustomIcon(DeleteIcon, {
                      isHover: hover,
                      hoverFillColor: themes.color.wfRed,
                    })
                  }
                  variant='tertiary'
                  size='xsmall'
                  style={{
                    marginRight: -6,
                  }}
                  appearance='plain'
                  onClick={deleteAnnotation}
                  loading={contextManager.loading}
                />
              )}
              {closeButton}
            </div>
          </DialogButtons>
        </DialogHeader>
        <DialogMessage>{note.text}</DialogMessage>
      </>
    );
  }

  // - PinStyled and PinDialog will be updated by updatePinPositions above, they
  // need to be silblings so that they can have different z-indexes, with PinDialog
  // always being above PinStyled
  return (
    <>
      <PinStyled
        expanded={expanded()}
        id={`pin-styled-${note.key}`}
        visible={note.visible && viewerControl.viewer.notePad.visible}
        left={note.coords?.left}
        top={note.coords?.top}
      >
        <ButtonStyled
          fontSize={size}
          shared={!note.isTemp}
          ordinal={' '}
          isFeature={isFeature}
          hovered={note.visible && hovered()}
          onClick={e => onPinClick(e)}
          onMouseEnter={() => viewerControl.HighlightPinTarget(note.key)}
          onMouseLeave={viewerControl.UnHighlightEverything}
          variant='secondary'
          icon='annotation'
          iconViewBox='0 0 40 40'
          appearance='plain'
        />
      </PinStyled>
      <PinDialog
        expanded={expanded()}
        id={`pin-dialog-${note.key}`}
        visible={note.visible && viewerControl.viewer.notePad.visible}
        left={note.coords?.left}
        top={note.coords?.top}
      >
        <DialogStyled
          visible={
            note.visible && viewerControl.viewer.notePad.visible && expanded()
          }
          width={PinConfig.pinDialogWidth}
          left={dialogLeft}
        >
          {shouldShowAnnotationText && (
            <div className='w-full border-b py-2 px-4 border-wfGrey350 flex justify-between'>
              <AnnotationItem
                className='font-extrabold cursor-default'
                text={
                  isActiveGeometry
                    ? i18next.t('Annotate Geometry')
                    : i18next.t('Annotate Part')
                }
                icon={isActiveGeometry ? <GeometryIcon /> : <PartIcon />}
              />
              {closeButton}
            </div>
          )}
          <div className='m-2'>{innerPinContent}</div>
        </DialogStyled>
      </PinDialog>
    </>
  );
});

Pin.propTypes = {
  note: PropTypes.shape({
    key: PropTypes.string,
    annotationId: PropTypes.string,
    visible: PropTypes.bool,
    text: PropTypes.string,
    timestamp: PropTypes.string,
    userId: PropTypes.string,
  }).isRequired,
};
